import React, { useEffect, useRef, useState } from 'react';

export interface DeleteMerchantModalProps {
  visible: boolean;
  merchantLabel: string;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
}

/**
 * Plain React modal (no Syncfusion Dialog) to avoid DOM reconciliation conflicts
 * with React 19 / React Router when navigating to `/merchants/:id`.
 */
export function DeleteMerchantModal({
  visible,
  merchantLabel,
  onClose,
  onConfirm,
}: DeleteMerchantModalProps): React.ReactElement | null {
  const panelRef = useRef<HTMLDivElement>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!visible) {
      setPending(false);
      setError(null);
      return;
    }
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape' && !pending) {
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    panelRef.current?.querySelector<HTMLButtonElement>('button')?.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, [visible, onClose, pending]);

  if (!visible) {
    return null;
  }

  const handleConfirm = (): void => {
    setError(null);
    setPending(true);
    void Promise.resolve(onConfirm())
      .then(() => {
        onClose();
      })
      .catch((e: unknown) => {
        setError(e instanceof Error ? e.message : 'Delete failed.');
      })
      .finally(() => {
        setPending(false);
      });
  };

  return (
    <div
      className="bo-modal-backdrop"
      role="presentation"
      onClick={() => {
        if (!pending) {
          onClose();
        }
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape' && !pending) {
          onClose();
        }
      }}
    >
      <div
        ref={panelRef}
        className="bo-modal-panel"
        role="dialog"
        aria-modal
        aria-labelledby="bo-delete-merchant-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="bo-delete-merchant-title" className="bo-modal-title">
          Delete merchant
        </h2>
        <p style={{ marginTop: 0 }}>
          Are you sure you want to delete <strong>{merchantLabel}</strong>? This action cannot be
          undone.
        </p>
        {error ? (
          <p className="bo-modal-error" role="alert">
            {error}
          </p>
        ) : null}
        <div className="bo-actions" style={{ marginTop: '1.25rem', justifyContent: 'flex-end' }}>
          <button type="button" className="bo-btn bo-btn-secondary" onClick={onClose} disabled={pending}>
            Cancel
          </button>
          <button
            type="button"
            className="bo-btn bo-btn-danger"
            onClick={handleConfirm}
            disabled={pending}
          >
            {pending ? 'Deleting…' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
}
